import React, { useState, useEffect } from "react";
import "../AdminScheduling.css";
import { buildTimeOptions, formatTimeFromMinutes } from "../timeUtils";
import { defaultTimelineRangeForDayKey as defaultScheduleTimelineRangeForDayKey } from "../scheduleSettingsUtils";

const AVAIL_TIME_OPTIONS = buildTimeOptions(6 * 60, 22 * 60, 30);
const HOUR_OPTIONS = buildTimeOptions(6 * 60, 22 * 60, 60);

function defaultTimelineRange(dayKey) {
  return defaultScheduleTimelineRangeForDayKey(dayKey);
}

export default function MasterDaySettingsModal({
  isOpen,
  onClose,
  dayKey,
  tutors,
  initialDayConfig,
  initialAvailability,
  onSave,
}) {
  const [activeTab, setActiveTab] = useState("daySettings"); // "daySettings" | "availability"

  const [isClosed, setIsClosed] = useState(false);
  const [timelineStartMin, setTimelineStartMin] = useState(14 * 60);
  const [timelineEndMin, setTimelineEndMin] = useState(21 * 60);

  // uid -> { uid, startMin, endMin, unavailable }
  const [availMap, setAvailMap] = useState({});

  useEffect(() => {
    if (!isOpen) return;

    const defaults = defaultTimelineRange(dayKey);
    const cfg = initialDayConfig || {};
    const start = Number(cfg.timelineStartMin);
    const end = Number(cfg.timelineEndMin);

    const safeStart = Number.isFinite(start) ? start : defaults.startMin;
    const safeEnd = Number.isFinite(end) && end > safeStart
      ? end
      : Math.max(safeStart + 60, defaults.endMin);

    setIsClosed(!!cfg.isClosed);
    setTimelineStartMin(safeStart);
    setTimelineEndMin(safeEnd);

    const map = {};
    (tutors || []).forEach((tutor) => {
      const found = (initialAvailability || []).find((a) => a.uid === tutor.id);
      if (found) {
        map[tutor.id] = {
          uid: tutor.id,
          startMin: Number(found.startMin),
          endMin: Number(found.endMin),
          unavailable: !!found.unavailable,
          hidden: !!found.hidden,
        };
      } else {
        map[tutor.id] = {
          uid: tutor.id,
          unavailable: true,
          hidden: false,
          startMin: defaults.startMin,
          endMin: defaults.endMin,
        };
      }
    });
    setAvailMap(map);
    setActiveTab("daySettings");
  }, [isOpen, dayKey, initialDayConfig, initialAvailability, tutors]);

  const updateAvail = (uid, field, value) => {
    setAvailMap((prev) => ({
      ...prev,
      [uid]: (() => {
        const current = prev[uid] || {};
        if (field === "unavailable") {
          return {
            ...current,
            unavailable: value,
            hidden: value ? true : current.hidden,
          };
        }
        if (field === "hidden") {
          if (current.unavailable) {
            return {
              ...current,
              hidden: true,
            };
          }
          return {
            ...current,
            hidden: value,
          };
        }
        return {
          ...current,
          [field]: value,
        };
      })(),
    }));
  };

  const handleSave = async () => {
    await onSave({
      dayConfig: {
        isClosed,
        timelineStartMin,
        timelineEndMin,
      },
      availabilityUpdates: Object.values(availMap),
    });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="modalOverlay" onMouseDown={onClose}>
      <div className="modalBox" style={{ width: 600, maxWidth: "95vw" }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="modalTitleRow">
          <div className="modalTitle">Master Settings — {String(dayKey || "").toUpperCase()}</div>
          <button className="modalCloseBtn" onClick={onClose}>X</button>
        </div>

        <div className="masterTabs">
          <button
            className={`masterTabBtn ${activeTab === "daySettings" ? "active" : ""}`}
            onClick={() => setActiveTab("daySettings")}
          >
            Day Timeline
          </button>
          <button
            className={`masterTabBtn ${activeTab === "availability" ? "active" : ""}`}
            onClick={() => setActiveTab("availability")}
          >
            Master Availability
          </button>
        </div>

        <div className="masterTabContent">
          {activeTab === "daySettings" && (
            <div className="formGrid">
              <div>Day Status</div>
              <select
                value={isClosed ? "closed" : "open"}
                onChange={(e) => setIsClosed(e.target.value === "closed")}
              >
                <option value="open">Open</option>
                <option value="closed">Closed</option>
              </select>

              <div>Timeline Start</div>
              <select
                value={timelineStartMin}
                onChange={(e) => {
                  const nextStart = Number(e.target.value);
                  setTimelineStartMin(nextStart);
                  if (timelineEndMin <= nextStart) {
                    setTimelineEndMin(Math.min(22 * 60, nextStart + 60));
                  }
                }}
              >
                {HOUR_OPTIONS.filter((t) => t <= timelineEndMin - 60).map((t) => (
                  <option key={t} value={t}>{formatTimeFromMinutes(t)}</option>
                ))}
              </select>

              <div>Timeline End</div>
              <select
                value={timelineEndMin}
                onChange={(e) => setTimelineEndMin(Number(e.target.value))}
              >
                {HOUR_OPTIONS.filter((t) => t >= timelineStartMin + 60).map((t) => (
                  <option key={t} value={t}>{formatTimeFromMinutes(t)}</option>
                ))}
              </select>
            </div>
          )}

          {activeTab === "availability" && (
            <div style={{ maxHeight: 400, overflowY: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ textAlign: "left", borderBottom: "1px solid #ddd" }}>
                    <th style={{ padding: 8 }}>Tutor</th>
                    <th style={{ padding: 8 }}>Status</th>
                    <th style={{ padding: 8 }}>Hide</th>
                    <th style={{ padding: 8 }}>Time Range</th>
                  </tr>
                </thead>
                <tbody>
                  {(tutors || []).map((tutor) => {
                    const av = availMap[tutor.id];
                    if (!av) return null;

                    return (
                      <tr key={tutor.id} style={{ borderBottom: "1px solid #f3f4f6" }}>
                        <td style={{ padding: 8, fontWeight: 600 }}>{tutor.name}</td>
                        <td style={{ padding: 8 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={av.unavailable}
                              onChange={(e) => updateAvail(tutor.id, "unavailable", e.target.checked)}
                            />
                            Unavailable
                          </label>
                        </td>
                        <td style={{ padding: 8 }}>
                          <label style={{ display: "flex", alignItems: "center", gap: 6, cursor: "pointer" }}>
                            <input
                              type="checkbox"
                              checked={av.unavailable ? true : av.hidden}
                              disabled={av.unavailable}
                              onChange={(e) => updateAvail(tutor.id, "hidden", e.target.checked)}
                            />
                            Hide
                          </label>
                        </td>
                        <td style={{ padding: 8 }}>
                          {!av.unavailable ? (
                            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                              <select
                                value={av.startMin}
                                onChange={(e) => updateAvail(tutor.id, "startMin", Number(e.target.value))}
                              >
                                {AVAIL_TIME_OPTIONS.map((tm) => (
                                  <option key={tm} value={tm}>{formatTimeFromMinutes(tm)}</option>
                                ))}
                              </select>
                              <span>to</span>
                              <select
                                value={av.endMin}
                                onChange={(e) => updateAvail(tutor.id, "endMin", Number(e.target.value))}
                              >
                                {AVAIL_TIME_OPTIONS.map((tm) => (
                                  <option key={tm} value={tm}>{formatTimeFromMinutes(tm)}</option>
                                ))}
                              </select>
                            </div>
                          ) : (
                            <span style={{ color: "#999", fontSize: 13 }}>—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </div>

        <div className="modalActions">
          <button className="btn" onClick={onClose}>Cancel</button>
          <button className="btn" onClick={handleSave}>Save Changes</button>
        </div>
      </div>
    </div>
  );
}
